import React, { useState, useEffect, useContext, useMemo, memo } from 'react'
import axios from 'axios'
import jwt_decode from 'jwt-decode'
import AuthContext from '../../../middlewares/AuthContext'
import Swal from 'sweetalert2'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faTableList, faTriangleExclamation, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import { DateTime } from 'luxon'
import 'chart.js/auto'
import { Chart as ChartJs } from 'chart.js'
import { Chart, Bar, Pie } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import ChartDataLabels from 'chartjs-plugin-datalabels'

ChartJs.register(ChartDataLabels)

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

// ── Types ──────────────────────────────────────────────────────────────────────
interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
  token     : string | null
}

interface DashboardSnap {
  activated : number
  renovated : number
  to_expire : number
  expired   : number
  available : number
  createdAt?: string
}

interface DailyUsage {
  label: string
  isPro: number
  noPro: number
}

interface MonthlyAvg {
  label      : string
  avgIsPro   : number
  avgNoPro   : number
}

interface ProductData {
  productName : string
  avgIsPro    : number
  avgNoPro    : number
}

interface MonthlyByProduct {
  label: string
  data : ProductData[]
}

interface MonthlyUsers {
  label: string
  isPro: number
  noPro: number
}

interface ProductItem {
  id           : number
  name         : string
  list_price   : number
  list_price_mx: number
}

interface PartnerItem {
  id          : number
  discount    : number
  discount_smp: number
  country     : string
}

// ── Constants ──────────────────────────────────────────────────────────────────
const PRODUCT_COLORS: Record<string, string> = {
  'Text2 barcode' : 'rgba(204,1,1,0.8)',
  'PDF Label Cut' : 'rgba(241,140,37,0.8)',
  'SMP'           : 'rgba(106,174,237,0.8)',
  'Codaly'        : 'rgba(33,33,33,0.8)',
}

const pieDatalabels = {
  backgroundColor : () => '#ffffff',
  formatter       : (val: number, ctx: any) => {
    const total = (ctx.chart.data.datasets[ctx.datasetIndex].data as number[])
      .reduce((a, b) => Number(a) + Number(b), 0)
    const pct = total > 0 ? ((Number(val) * 100) / total).toFixed(2) : '0.00'
    return `C: ${parseFloat(String(val)).toFixed(2)}\nP: ${pct}%`
  },
  borderRadius : 25,
  borderWidth  : 3,
  color        : 'black',
  font         : { weight: 'bold' as const },
  padding      : 6,
}

const chartCard = {
  backgroundColor : '#fff',
  borderRadius    : 16,
  boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
  border          : '1px solid #f1f5f9',
  padding         : '1.5rem',
  marginBottom    : '1.5rem',
}

// ── TdPrices ───────────────────────────────────────────────────────────────────
interface TdPricesProps {
  product: ProductItem | null
  partner: PartnerItem | null
}

const TdPrices = memo<TdPricesProps>(({ product, partner }) => {
  if (!partner || !product) return <td>—</td>

  const dsc        = product.id === 2 ? partner.discount_smp : partner.discount
  const isMx       = ['mexico', 'méxico'].includes(partner.country.toLowerCase())
  const base_price = isMx ? product.list_price_mx : product.list_price
  const currency   = isMx ? 'mxn' : 'usd'
  const final      = base_price - base_price * (dsc / 100)

  return (
    <td>
      <span>{dsc}%</span>
      <b> · </b>
      <span>= ${final.toFixed(2)} {currency}</span>
    </td>
  )
})

// ── StatCard ───────────────────────────────────────────────────────────────────
interface StatCardProps {
  value : number
  label : string
  sub?  : string
  icon  : any
  color : string
  delay : string
}

const StatCard: React.FC<StatCardProps> = ({ value, label, sub, icon, color, delay }) => (
  <div className="col-sm-6 col-md-3 mb-3 animate__animated animate__fadeInUp" style={{ animationDelay: delay }}>
    <div style={{
      backgroundColor : '#fff',
      borderRadius    : 16,
      boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
      border          : '1px solid #f1f5f9',
      overflow        : 'hidden',
      height          : '100%',
    }}>
      <div style={{ height: 3, backgroundColor: color }} />
      <div style={{ padding: '1.25rem 1.25rem 1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {label}
            </p>
            <p style={{ margin: 0, fontSize: '2rem', fontWeight: 900, color: '#1e293b', lineHeight: 1.1 }}>
              {value}
            </p>
            {sub && (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: '#64748b' }}>{sub}</p>
            )}
          </div>
          <div style={{
            width           : 44,
            height          : 44,
            borderRadius    : 12,
            backgroundColor : `${color}18`,
            display         : 'flex',
            alignItems      : 'center',
            justifyContent  : 'center',
            flexShrink      : 0,
          }}>
            <FontAwesomeIcon icon={icon} style={{ color, fontSize: '1.2rem' }} />
          </div>
        </div>
      </div>
    </div>
  </div>
)

// ── Skeleton ───────────────────────────────────────────────────────────────────
const Skeleton: React.FC = () => (
  <div className="row animate__animated animate__fadeIn">
    {[0, 1, 2, 3].map(i => (
      <div key={i} className="col-sm-6 col-md-3 mb-3">
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
          height          : 110,
        }}>
          <div style={{ height: 3, backgroundColor: '#e2e8f0' }} />
          <div style={{ padding: '1.25rem' }}>
            <span className="placeholder-glow d-block mb-2">
              <span className="placeholder col-5" style={{ borderRadius: 6 }} />
            </span>
            <span className="placeholder-glow d-block">
              <span className="placeholder col-3" style={{ borderRadius: 6, height: 28 }} />
            </span>
          </div>
        </div>
      </div>
    ))}
  </div>
)

// ── AdminDashboard ─────────────────────────────────────────────────────────────
const AdminDashboard: React.FC = () => {
  const { getHeaders, token }    = useContext(AuthContext) as AuthCtx
  const tokenDecode: any         = useMemo(() => token ? jwt_decode(token) : null, [token])
  const isAdmin                  = useMemo(() => tokenDecode?.roles?.some((r: any) => r.name === 'admin'), [tokenDecode])
  const { t, i18n }              = useTranslation()

  const [dashboardOne, setDashboardOne]         = useState<DashboardSnap | null>(null)
  const [snapshots, setSnapshots]               = useState<DashboardSnap[]>([])
  const [today, setToday]                       = useState(DateTime.now({ zone: 'utc' }))
  const [monthlyAvg, setMonthlyAvg]             = useState<MonthlyAvg[] | null>(null)
  const [monthlyByProduct, setMonthlyByProduct] = useState<MonthlyByProduct[] | null>(null)
  const [monthlyUsers, setMonthlyUsers]         = useState<MonthlyUsers[] | null>(null)
  const [dailyUsage, setDailyUsage]             = useState<DailyUsage[] | null>(null)
  const [products, setProducts]                 = useState<ProductItem[]>([])
  const [partner, setPartner]                   = useState<PartnerItem | null>(null)

  useEffect(() => {
    const fetchAll = async () => {
      const headers = await getHeaders()

      // dashboard + prices in parallel
      const [dashRes, priceRes] = await Promise.allSettled([
        axios.get(`${url}dashboards`, { headers }),
        axios.get(`${url}dashboards/prices`, { headers }),
      ])

      if (dashRes.status === 'fulfilled') {
        const { message, dashboards } = dashRes.value.data
        setDashboardOne(message)
        setSnapshots(dashboards[0] ?? [])
      } else {
        Swal.fire({ icon: 'error', title: dashRes.reason?.response?.data?.response || 'Error' })
      }

      if (priceRes.status === 'fulfilled') {
        setProducts(priceRes.value.data.message ?? [])
        setPartner(priceRes.value.data.partner ?? null)
      }

      if (isAdmin) {
        try {
          const avgRes = await axios.get(`${url}dashboards/average`, { headers })
          const d = avgRes.data
          setDailyUsage(       d.message?.length  ? d.message  : null)
          setMonthlyAvg(       d.message2?.length ? d.message2 : null)
          setMonthlyByProduct( d.message3?.length ? d.message3 : null)
          setMonthlyUsers(     d.message4?.length ? d.message4 : null)
        } catch (err: any) {
          Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
        }
      }
    }

    fetchAll()

    const interval = setInterval(() => setToday(prev => prev.plus({ seconds: 1 })), 1000)
    return () => clearInterval(interval)
  }, [])

  // ── Chart data — reactive to language ────────────────────────────────────────
  const lineChartData = useMemo(() => {
    if (!snapshots.length) return null
    return {
      // FIX: parse as UTC so day labels match the server's date, not the browser's local timezone
      labels: snapshots.map(e => DateTime.fromISO(e.createdAt!, { zone: 'utc' }).toFormat('dd')),
      datasets: [
        { label: t('dashboard.activated'), data: snapshots.map(e => e.activated), backgroundColor: '#6eb446', borderColor: '#3b7d3c', borderWidth: 2 },
        { label: t('dashboard.renovated'), data: snapshots.map(e => e.renovated), backgroundColor: '#0ea5e9', borderColor: '#0284c7', borderWidth: 2 },
        { label: t('dashboard.to_expire'), data: snapshots.map(e => e.to_expire), backgroundColor: '#f59e0b', borderColor: '#d97706', borderWidth: 2 },
        { label: t('dashboard.expired'),   data: snapshots.map(e => e.expired),   backgroundColor: '#ef4444', borderColor: '#dc2626', borderWidth: 2 },
        { label: t('dashboard.available'), data: snapshots.map(e => e.available), backgroundColor: '#3b82f6', borderColor: '#2563eb', borderWidth: 2 },
      ],
    }
  }, [snapshots, i18n.language])

  const barChartData = useMemo(() => {
    if (!dailyUsage?.length) return null
    return {
      labels  : dailyUsage.map(e => e.label),
      datasets: [
        { label: 'Pro',  data: dailyUsage.map(e => e.isPro), backgroundColor: 'rgba(110,180,70,0.85)' },
        { label: 'Free', data: dailyUsage.map(e => e.noPro), backgroundColor: 'rgba(239,68,68,0.75)'  },
      ],
    }
  }, [dailyUsage])

  if (!dashboardOne) return <Skeleton />

  const total = dashboardOne.activated + dashboardOne.renovated

  return (
    <>
      <h2 className="mb-3" style={{ fontSize: '1.05rem', fontWeight: 700, color: '#475569' }}>
        {t('dashboard.today')}: {today.setLocale(i18n.language).toFormat('yyyy-LLLL-dd HH:mm:ss')}
      </h2>

      {/* ── Stat cards ──────────────────────────────────────────────────────── */}
      <div className="row animate__animated animate__fadeIn">
        <StatCard
          value={total}
          label={t('dashboard.activated')}
          sub={`${dashboardOne.activated} ${t('dashboard.licenses_activated')} · ${dashboardOne.renovated} ${t('dashboard.licenses_renovated')}`}
          icon={faCheckCircle}
          color="#6eb446"
          delay="0s"
        />
        <StatCard
          value={dashboardOne.to_expire}
          label={t('dashboard.to_expire')}
          sub={t('dashboard.licenses_to_expire')}
          icon={faTriangleExclamation}
          color="#f59e0b"
          delay="0.05s"
        />
        <StatCard
          value={dashboardOne.expired}
          label={t('dashboard.expired')}
          sub={t('dashboard.licenses_expired')}
          icon={faXmarkCircle}
          color="#ef4444"
          delay="0.1s"
        />
        <StatCard
          value={dashboardOne.available}
          label={t('dashboard.available')}
          sub={t('dashboard.licenses_available')}
          icon={faTableList}
          color="#3b82f6"
          delay="0.15s"
        />
      </div>

      {/* ── Line chart — monthly snapshots ──────────────────────────────────── */}
      {lineChartData && (
        <div style={{ ...chartCard, height: 300 }} className="animate__animated animate__fadeIn">
          <Chart
            type="line"
            data={lineChartData}
            options={{
              maintainAspectRatio : false,
              responsive          : true,
              plugins: {
                datalabels : { display: false },
                legend     : { position: 'top' },
                title      : { display: true, text: today.setLocale(i18n.language).toLocaleString({ month: 'long' }) },
              },
              elements: {
                point : { radius: 5, borderWidth: 2 },
                line  : { borderWidth: 2 },
              },
            }}
          />
        </div>
      )}

      {/* ── Bar chart — daily app usage (admin only) ─────────────────────────── */}
      {isAdmin && barChartData && (
        <div style={{ ...chartCard, height: 300 }} className="animate__animated animate__fadeIn">
          <Bar
            data={barChartData}
            options={{
              maintainAspectRatio : false,
              responsive          : true,
              plugins: {
                legend     : { position: 'top' },
                title      : { display: true, text: 'Aplicaciones en uso' },
                datalabels : {
                  backgroundColor : () => '#ffffff',
                  formatter       : (val: number) => `${val}`,
                  borderRadius    : 15,
                  borderWidth     : 2,
                  color           : 'black',
                  font            : { weight: 'bold' as const },
                  padding         : 2,
                },
              },
            }}
          />
        </div>
      )}

      {/* ── Pie charts — avg active users per month (admin only) ─────────────── */}
      {isAdmin && monthlyAvg && (
        <div style={{ ...chartCard }} className="animate__animated animate__fadeIn">
          <p className="text-center fw-bold mb-3">Promedio de usuarios activos por mes</p>
          <div className="row">
            {monthlyAvg.map((e, i) => (
              <div key={`ma-${i}`} className="col-md-4 mb-3" style={{ height: 280 }}>
                <Pie
                  data={{
                    labels  : ['Pro', 'Free'],
                    datasets: [{
                      label          : 'Promedio',
                      data           : [e.avgIsPro, e.avgNoPro],
                      backgroundColor: ['rgba(110,180,70,0.85)', 'rgba(239,68,68,0.75)'],
                      borderColor    : ['rgba(110,180,70,1)',    'rgba(239,68,68,1)'],
                      borderWidth    : 1,
                    }],
                  }}
                  options={{
                    maintainAspectRatio : false,
                    responsive          : true,
                    plugins: {
                      legend     : { position: 'top' },
                      title      : { display: true, text: e.label },
                      datalabels : pieDatalabels,
                    },
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Pie charts — avg by product (admin only) ─────────────────────────── */}
      {isAdmin && monthlyByProduct && (
        <div style={{ ...chartCard }} className="animate__animated animate__fadeIn">
          <p className="text-center fw-bold mb-3">Promedio de usuarios activos por aplicación</p>
          <div className="row">
            {monthlyByProduct.map((e, i) => {
              const knownProducts = ['Text2 barcode', 'PDF Label Cut', 'SMP', 'Codaly']
              const labels  = e.data.map(f => f.productName)
              const colors  = e.data.map(f => PRODUCT_COLORS[f.productName] ?? 'rgba(150,150,150,0.8)')
              const dataPro  = e.data.map(f => knownProducts.includes(f.productName) ? f.avgIsPro  : 0)
              const dataFree = e.data.map(f => knownProducts.includes(f.productName) ? f.avgNoPro : 0)
              const pieOpts  = (title: string) => ({
                maintainAspectRatio : false as const,
                responsive          : true,
                plugins: {
                  legend     : { position: 'top' as const },
                  title      : { display: true, text: title },
                  datalabels : pieDatalabels,
                },
              })
              return (
                <div key={`mbp-${i}`} className="col-md-4 mb-4" style={{ borderRight: '1px solid #f1f5f9' }}>
                  <div style={{ height: 280 }}>
                    <Pie
                      data={{ labels, datasets: [{ data: dataPro,  backgroundColor: colors, borderWidth: 1 }] }}
                      options={pieOpts(`${e.label} PRO`)}
                    />
                  </div>
                  <div style={{ height: 280, marginTop: '1.5rem' }}>
                    <Pie
                      data={{ labels: labels.map(l => `${l} Free`), datasets: [{ data: dataFree, backgroundColor: colors, borderWidth: 1 }] }}
                      options={pieOpts(`${e.label} FREE`)}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Pie charts — total users PRO / FREE (admin only) ─────────────────── */}
      {isAdmin && monthlyUsers && (
        <div style={{ ...chartCard }} className="animate__animated animate__fadeIn">
          <p className="text-center fw-bold mb-3">Cantidad de usuarios PRO / FREE</p>
          <div className="row">
            {monthlyUsers.map((e, i) => (
              <div key={`mu-${i}`} className="col-md-4 mb-3" style={{ height: 280 }}>
                <Pie
                  data={{
                    labels  : ['Pro', 'Free'],
                    datasets: [{
                      label          : 'users',
                      data           : [e.isPro, e.noPro],
                      backgroundColor: ['rgba(110,180,70,0.85)', 'rgba(239,68,68,0.75)'],
                      borderColor    : ['rgba(110,180,70,1)',    'rgba(239,68,68,1)'],
                      borderWidth    : 1,
                    }],
                  }}
                  options={{
                    maintainAspectRatio : false,
                    responsive          : true,
                    plugins: {
                      legend     : { position: 'top' },
                      title      : { display: true, text: e.label },
                      datalabels : pieDatalabels,
                    },
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Prices table ─────────────────────────────────────────────────────── */}
      <div style={{ ...chartCard, padding: 0, overflow: 'hidden' }} className="mb-5 animate__animated animate__fadeIn">
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #f1f5f9' }}>
          <h5 className="m-0 fw-bold" style={{ color: '#1e293b' }}>Prices</h5>
          {tokenDecode?.prepay !== undefined && (
            <span style={{ fontSize: '0.78rem', color: '#6eb446', fontWeight: 600 }}>
              {tokenDecode.prepay ? 'Prepago' : 'Pospago'}
            </span>
          )}
        </div>
        <div style={{ padding: '0 1rem 1rem' }}>
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Precio</th>
                <th>Descuento · = Precio Distribuidor</th>
              </tr>
            </thead>
            <tbody>
              {products.map((e, i) => (
                <tr key={`product-${i}`}>
                  <td>{e.name}</td>
                  {!partner && (
                    <td>${e.list_price_mx} mxn <b>–</b> ${e.list_price} usd</td>
                  )}
                  {partner && (
                    ['mexico', 'méxico'].includes(partner.country.toLowerCase())
                      ? <td>${e.list_price_mx} mxn</td>
                      : <td>${e.list_price} usd</td>
                  )}
                  <TdPrices product={e} partner={partner} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

export default AdminDashboard
